import { base64EncodeForExternalApp, serverConfig } from "oute-ds-utils";
import {
  AGENT_ANNOTATION,
  AGENT_TOOL_ANNOTATION,
  CONTENT_ANNOTATION,
  EMAIL_ANNOTATION,
  FORM_ANNOTATION,
  PORTAL_ANNOTATION,
  QUERY_PARAM_KEY,
  SEQUENCE_ANNOTATION,
  SHEET_ANNOTATION,
  WORKFLOW_ANNOTATION,
} from "../constants/common-constants";

// Free of React, redux and the router so /open can resolve a share link before the app
// mounts; useAssetNavigations reads the same table, which is what stops the two drifting.

// Lazy so the value reflects the environment at call time rather than at import.
export const APP_BASE_URLS = {
  workflow: () => serverConfig.WC_URL,
  sequence: () => process.env.REACT_APP_SEQUENCE_STUDIO_URL,
  form: () => serverConfig.FORMFLOW_URL,
  sheet: () => serverConfig.SHEET_URL,
  content: () => serverConfig.BLOG_URL,
  emailTemplate: () => serverConfig.EMAIL_TEMPLATE_URL,
  agent: () => process.env.REACT_APP_AGENT_STUDIO_URL,
  portal: () => process.env.REACT_APP_PORTAL_STUDIO_URL,
  agentWorkflow: () => serverConfig.AGENT_WORKFLOW_URL,
};

export const APP_URL_BY_ANNOTATION: Record<string, () => string | undefined> = {
  [WORKFLOW_ANNOTATION]: APP_BASE_URLS.workflow,
  [SEQUENCE_ANNOTATION]: APP_BASE_URLS.sequence,
  [FORM_ANNOTATION]: APP_BASE_URLS.form,
  [SHEET_ANNOTATION]: APP_BASE_URLS.sheet,
  [CONTENT_ANNOTATION]: APP_BASE_URLS.content,
  // The template app serves an individual asset under /asset; its root is the gallery.
  [EMAIL_ANNOTATION]: () => `${APP_BASE_URLS.emailTemplate()}/asset`,
  [AGENT_ANNOTATION]: APP_BASE_URLS.agent,
  [PORTAL_ANNOTATION]: APP_BASE_URLS.portal,
  [AGENT_TOOL_ANNOTATION]: APP_BASE_URLS.agentWorkflow,
};

// An unset env var stringifies to "undefined" and would otherwise sail through as a URL.
const isConfiguredUrl = (url: unknown): url is string =>
  typeof url === "string" &&
  !!url &&
  url !== "undefined" &&
  !url.startsWith("undefined");

export const resolveAppBaseUrl = (annotation?: unknown): string | null => {
  // hasOwnProperty because `annotation` arrives from a URL anyone can craft: plain
  // indexing would resolve "constructor" and "toString" off Object.prototype.
  if (
    typeof annotation !== "string" ||
    !Object.prototype.hasOwnProperty.call(APP_URL_BY_ANNOTATION, annotation)
  ) {
    return null;
  }
  const url = APP_URL_BY_ANNOTATION[annotation]();
  return isConfiguredUrl(url) ? url : null;
};

// Receiving apps decode this `q` with a bare atob, so the encoding is fixed.
export const withEncodedPayload = (
  baseUrl: string,
  payload: unknown,
  extraParams?: Record<string, string>,
): string => {
  let url = `${baseUrl}?${QUERY_PARAM_KEY}=${base64EncodeForExternalApp(payload)}`;
  const search = extraParams ? new URLSearchParams(extraParams).toString() : "";
  if (search) url += `&${search}`;
  return url;
};

// null means "not routable statically" — callers hand those to the authenticated
// resolver rather than guessing a destination.
export const buildAssetAppUrl = (
  annotation: unknown,
  payload: unknown,
  extraParams?: Record<string, string>,
): string | null => {
  const baseUrl = resolveAppBaseUrl(annotation);
  return baseUrl ? withEncodedPayload(baseUrl, payload, extraParams) : null;
};
